import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { ASSETS } from "@/lib/assets";

const HERO_IMG = ASSETS.heroPortrait;

const lineReveal = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.5 + i * 0.12 },
  }),
};

const Line = ({ children, i }) => (
  <span className="line-mask">
    <motion.span custom={i} variants={lineReveal} initial="hidden" animate="visible" className="block">
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={HERO_IMG} alt="Darrel Boye behind an ARRI cinema camera" className="w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-black/50" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/30 to-[#050505]/50" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/80 via-transparent to-transparent" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 max-w-[1400px] mx-auto px-6 sm:px-10 min-h-screen flex flex-col justify-end pb-24 pt-32">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 1 }}
          className="font-body text-xs uppercase tracking-[0.4em] text-[#EF233C] mb-6"
        >
          Darrel Boye — DBoye Creatives
        </motion.p>

        <h1 className="font-head font-700 uppercase text-white text-5xl sm:text-7xl lg:text-8xl leading-[0.9] tracking-tighter">
          <Line i={0}>Cinematic</Line>
          <Line i={1}>
            <span className="text-stroke-red">Storytelling</span>
          </Line>
          <Line i={2}>For Bold Brands</Line>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 1 }}
          className="mt-10 flex flex-col sm:flex-row sm:items-end gap-6 sm:gap-14"
        >
          <p className="font-body text-base text-gray-300 max-w-md leading-relaxed">
            Director of Photography, director and editor crafting films, photography and design that move people. Book a session, a class, or a full production.
          </p>
          <button
            data-testid="hero-book-btn"
            onClick={() => document.getElementById("booking")?.scrollIntoView({ behavior: "smooth" })}
            className="group shrink-0 flex items-center gap-4 px-8 py-4 bg-[#D90429] hover:bg-[#EF233C] transition-colors duration-300"
          >
            <span className="font-body uppercase tracking-[0.15em] text-sm text-white">Book a Service</span>
            <ArrowDown className="w-4 h-4 text-white group-hover:translate-y-1 transition-transform duration-300" />
          </button>
        </motion.div>
      </motion.div>
    </section>
  );
};
